/**
 * Directory badges the footer carries.
 *
 * Each of these directories gives AnyAPI a free listing only while its badge is
 * displayed on getanyapi.com, and StartupTrusted, ToolFame, LaunchIgniter,
 * Findly and TinyShelf re-check it on a schedule and unpublish the listing when
 * it disappears. StartupBase is the exception: its badge is optional and only
 * shortens their review queue. So
 * `href` and `src` come from the directory's own embed snippet, UTMs and
 * `?ref=` included, and must stay verbatim: no added `nofollow`/`sponsored`
 * rel, no extra query string.
 *
 * What is ours to choose is the rendered size. Each badge is shrunk to a 24px
 * line so the row reads as one line of fine print rather than five ads. The
 * directories pin the markup, not the dimensions, and their checks read the
 * markup rather than the rendered page.
 *
 * Alt text is ASCII-hyphenated even where a supplied snippet used an em dash,
 * because `scripts/check-dashes.sh` fails the build on the glyph and alt is not
 * one of the pinned attributes.
 *
 * Every `src` is written out as a literal rather than mapped from an array.
 * `src/routes/-cache-headers.test.ts` scans these files for absolute image URLs
 * to prove the CSP's `img-src` in `public/_headers` covers every origin the app
 * loads, and a value read from a variable is invisible to that scan. Adding a
 * badge here means adding its origin there.
 *
 * To drop a directory, delete its `<a>` below. Nothing else references them.
 */
const CARD = 'inline-flex opacity-70';

export const LaunchBadges = () => (
  <div className="flex flex-wrap items-center gap-4">
    {/* Supplied at 220x48. The art is 55:12, so 110x24 is the only smaller
        exact pair above 55x12. Its light theme draws its own white card, so one
        asset stays legible on both the light and the dark footer. */}
    <a href="https://www.scrolllaunch.com/products/anyapi?ref=badge" target="_blank" rel="noopener" className={CARD}>
      <img
        src="https://www.scrolllaunch.com/api/badge/anyapi?variant=launched&theme=light"
        alt="AnyAPI - Featured on ScrollLaunch"
        width="110"
        height="24"
        loading="lazy"
        decoding="async"
      />
    </a>

    {/* Supplied at 244x56. The art is 61:14, so 122x28 is the largest
        whole-pixel reduction. They ship a white card and a black one, so both
        render and the shared `light-only`/`dark-only` classes pick one:
        branching on the theme in JSX mismatches on hydration. */}
    <a
      href="https://nicklaunches.com/products/anyapi-2/?utm_source=getanyapi.com&utm_medium=badge&utm_campaign=featured"
      target="_blank"
      rel="noopener"
      className={CARD}
    >
      <img
        src="https://nicklaunches.com/badges/featured.png"
        alt="AnyAPI on Nick Launches"
        width="122"
        height="28"
        loading="lazy"
        decoding="async"
        className="light-only"
      />
      <img
        src="https://nicklaunches.com/badges/featured-dark.png"
        alt="AnyAPI on Nick Launches"
        width="122"
        height="28"
        loading="lazy"
        decoding="async"
        className="dark-only"
      />
    </a>

    {/* Supplied at 212x55, which has no smaller whole-pixel pair, so this is the
        nearest one on the row's 24px line. */}
    <a href="https://launchigniter.com/product/anyapi?ref=badge-anyapi" target="_blank" rel="noopener" className={CARD}>
      <img
        src="https://launchigniter.com/api/badge/anyapi?theme=light"
        alt="Featured on LaunchIgniter"
        width="92"
        height="24"
        loading="lazy"
        decoding="async"
      />
    </a>

    {/* Supplied at 226x51, also with no exact smaller pair. Of their three
        styles the dark card is the one that reads on our footer in both
        themes, so it is the single asset here. */}
    <a href="https://startuptrusted.com" target="_blank" rel="noopener" className={CARD}>
      <img
        src="https://startuptrusted.com/api/badge?type=verified&style=dark"
        alt="Verified by StartupTrusted"
        width="106"
        height="24"
        loading="lazy"
        decoding="async"
      />
    </a>

    {/* Their snippet sizes by CSS height alone (54px, width auto) instead of
        giving pixel dimensions, so this keeps the SVG's own ratio at the row's
        line height. */}
    <a href="https://toolfame.com/item/anyapi" target="_blank" rel="noopener" className={CARD}>
      <img
        src="https://toolfame.com/badge-light.svg"
        alt="Featured on toolfame.com"
        width="94"
        height="24"
        loading="lazy"
        decoding="async"
      />
    </a>
    {/* Supplied at 175x55 against art whose own viewBox is 139x44, so their
        snippet already stretches it slightly. 70x22 keeps their ratio, which is
        what their check reads, and lands on the row's line. */}
    <a href="https://findly.tools/anyapi?utm_source=anyapi" target="_blank" rel="noopener" className={CARD}>
      <img
        src="https://findly.tools/badges/findly-tools-badge-light.svg"
        alt="Featured on Findly.tools"
        width="70"
        height="22"
        loading="lazy"
        decoding="async"
      />
    </a>

    {/* Supplied at 216x64, exactly 27:8, so 81x24 is a whole-pixel reduction
        onto the row's line. TinyShelf grants the dofollow link only while this
        is up, and it must not carry a nofollow rel. Of their three variants the
        light card is the one that reads on both the light and the dark footer;
        their dark card disappears into the dark one. */}
    <a href="https://www.tinyshelf.co" target="_blank" rel="noopener" className={CARD}>
      <img
        src="https://www.tinyshelf.co/badge/tinyshelf-badge-light-5ca4026a.svg"
        alt="Featured on tinyshelf"
        width="81"
        height="24"
        loading="lazy"
        decoding="async"
      />
    </a>

    {/* Supplied at 300x80, exactly 15:4, so 90x24 is a whole-pixel reduction.
        StartupBase does not require this; it only shortens their review queue
        from ten weeks to four. */}
    <a
      href="https://startupbase.io/products/anyapi?utm_source=startupbase&utm_medium=badge&utm_campaign=featured-badge-light"
      target="_blank"
      rel="noopener"
      className={CARD}
    >
      <img
        src="https://statics.startupbase.io/site/badges/featured-on-sb.svg"
        alt="Featured on StartupBase"
        width="90"
        height="24"
        loading="lazy"
        decoding="async"
      />
    </a>
  </div>
);
